import React from 'react';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { PagerConnector } from '../../../components/general/pager-connector/pager-connector';
import { useAppActions, useAppState } from '../../../overmind';
import { ActivationStatus, FetchType } from '../../../enums';
import { PersonActivationStatusIcon } from './freemium-people.cms.helpers';
import { IPeopleTableCMSLayoutProps } from './freemium-people.cms.models';
import { PersonListItem } from '../../../models/view/employee-search-response';
import { Button, Chip, formatDate, KebabMenu, MenuItem, Pager, Table, TableColumn, TableRow, useMatchScreenWidth } from '@keplerco/core';
import { PagePath } from '../../../navigation/navigation.enums';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import styles from './freemium-people.module.css';
import { ActivationStatusDescription } from '../../../enums/activation-status';

export function FreemiumPeopleTableCMSLayout(props: IPeopleTableCMSLayoutProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const keplerNavigate = useKeplerNavigate();
  const isMobile = useMatchScreenWidth('mobile');

  // tables
  async function onSortHandler(column: any) {
    actions.startLoader({ path: props.path, type: FetchType.PageFetching });

    const isCurrentlyAscending = props.request.sortAscending;
    const currentSortField = props.request.sortField;

    const newSortDirection = currentSortField === column ? !isCurrentlyAscending : true;

    props.setRequest({ ...props.request, sortField: column, sortAscending: newSortDirection });
  }

  async function onPageChangeHandler(page: number) {
    if (page === props.request.page) return;
    props.setSelectedRows([]);

    actions.startLoader({ path: props.path, type: FetchType.PageFetching });
    props.setRequest({ ...props.request, page });
  }

  async function onClickPersonHandler(id: string | undefined) {
    if (!id) return;

    actions.startLoader({ path: props.path, type: FetchType.Custom });
    props.setPersonToManage({ show: true, data: id });
    actions.stopLoader(props.path);
  }

  // kebab menus
  async function onClickArchiveHandler(person: PersonListItem) {
    if (!person.id) return;
    props.setPersonToArchive(person);
  }

  async function onClickUnarchiveHandler(person: PersonListItem) {
    if (!person.id || person.activationStatus !== ActivationStatus.Archived) return;

    props.setLoadingPeople([...props.loadingPeople, person.id]);
    await actions.displayPaywall('archive user');
    props.setLoadingPeople([...props.loadingPeople.filter(pid => person.id !== pid)]);
  }

  async function onClickDeleteHandler(person: PersonListItem) {
    if (!person.id) return;
    props.setPersonToDelete(person);
  }

  function generateKebabMenuItems(person: PersonListItem): MenuItem[] {
    const isArchived = person.activationStatus === ActivationStatus.Archived;
    const isNotInvited = person.activationStatus === ActivationStatus.NotInvited;
    const isInactive = person.activationStatus === ActivationStatus.Inactive;

    return [
      {
        label: 'Manage',
        onClick: () => onClickPersonHandler(person.id),
        disabled: isArchived,
      },
      {
        label: isArchived ? 'Unarchive' : 'Archive',
        onClick: () => (isArchived ? onClickUnarchiveHandler(person) : onClickArchiveHandler(person)),
      },
      {
        label: 'Delete',
        onClick: () => onClickDeleteHandler(person),
        disabled: !isArchived,
      },
      {
        label: 'Send activation invitation',
        onClick: () => props.onClickSendActivationInvitation({ emails: [person.email!], shouldClearRows: false }),
        disabled: !isNotInvited || !person.email,
      },
      {
        label: 'Send activation reminder',
        onClick: () => props.onClickSendActivationReminder({ emails: [person.email!], shouldClearRows: false }),
        disabled: !isInactive || !person.email,
      },
    ];
  }

  return (
    <Table
      selectableRows
      currentSortBy={String(props.request.sortField)}
      onSort={onSortHandler}
      currentSortDirection={props.request.sortAscending ? 'Ascending' : 'Descending'}
      selectedRows={props.selectedRows}
      onRowSelection={props.setSelectedRows}
      emptyState={
        <EmptyState
          title="No one has been added yet"
          buttonElements={[
            <Button key="Bulk Upload" type="button" onClick={() => keplerNavigate(`${PagePath.freemiumUserManagementBase}${PagePath.freemiumUserManagementPeopleBulkUpload.replace(':companySlug', companyVariables.slug!)}`)}>
              Bulk Upload
            </Button>,
          ]}
        />
      }
      onConfigClicked={() => props.setManageColumns({ open: true })}
      footerContent={
        <PagerConnector onPageChange={onPageChangeHandler} defaultPageNumber={props.request.page ?? 1} pageCount={props.data?.totalPages ?? 1}>
          {connector => {
            return <Pager {...connector} />;
          }}
        </PagerConnector>
      }
    >
      {props.data?.employees.map(person => {
        const isLoading = props.loadingPeople.includes(person.id!);

        return (
          <TableRow
            configCell={() => {
              return (
                <span key={person.id} style={{ visibility: isLoading ? 'hidden' : undefined, pointerEvents: isLoading ? 'none' : undefined }}>
                  <KebabMenu items={generateKebabMenuItems(person)} />
                </span>
              );
            }}
            id={person.id!}
            key={person.id}
            isLoading={isLoading}
          >
            <TableColumn id="Name" label="Name" sortable>
              <div className={styles.peopleNameCell}>
                <PersonActivationStatusIcon status={person.activationStatus} />
                {`${person.firstName} ${person.lastName}`}
              </div>
            </TableColumn>


            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Email')?.selected} id="Email" label="Email">
              {isMobile && person.email.length > 14 ? `${person.email.slice(0, 14)}...` : person.email}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Jobtitle')?.selected} id="Jobtitle" label="Jobtitle">
              {isMobile && person.jobtitle.length > 14 ? `${person.email.slice(0, 14)}...` : person.jobtitle}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'Country')?.selected} id="Country" label="Country">
              {person.country.name}
            </TableColumn>

            <TableColumn hidden={!props.columnConfiguration.find(column => column.key === 'Status')?.selected} id="Status" label="Status">
              {person.activationStatus === ActivationStatus.NotInvited && <Chip themeColour="r">{ActivationStatusDescription.NotInvited}</Chip>}
              {person.activationStatus === ActivationStatus.Inactive && <Chip themeColour="a">{ActivationStatusDescription.Inactive}</Chip>}
              {person.activationStatus === ActivationStatus.Active && <Chip themeColour="g">{ActivationStatusDescription.Active}</Chip>}
              {person.activationStatus === ActivationStatus.Archived && <Chip>{ActivationStatusDescription.Archived}</Chip>}
            </TableColumn>

            <TableColumn sortable hidden={!props.columnConfiguration.find(column => column.key === 'DateCreated')?.selected} id="DateCreated" label="Date Created">
              {!!person.dateCreated && formatDate(new Date(person.dateCreated))}
            </TableColumn>
          </TableRow>
        );
      })}
    </Table>
  );
}
